"use client";

import { CaseStudy, getPublishedCaseStudies } from "@/app/actions/case-studies";
import { FadeInStaggerItem, Stagger } from "@/components/ui/motion";
import TiltCard from "@/components/ui/TiltCard";
import { Loader2 } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

const CATEGORIES = [
    "All",
    "Gen AI",
    "Agentic AI",
    "Website",
    "Mobile App",
    "SaaS",
    "Marketplace"
];

interface WorkGridProps {
    initialProjects: CaseStudy[];
}

interface CacheEntry {
    projects: CaseStudy[];
    hasMore: boolean;
    lastCreatedAt?: number;
    isInitialLoaded: boolean;
}

export default function WorkGrid({ initialProjects }: WorkGridProps) {
    const [activeCategory, setActiveCategory] = useState("All");
    const [loading, setLoading] = useState(false);

    // Cache for all categories
    const [cache, setCache] = useState<Record<string, CacheEntry>>({
        "All": {
            projects: initialProjects,
            hasMore: initialProjects.length === 10,
            lastCreatedAt: initialProjects.length > 0 ? initialProjects[initialProjects.length - 1].createdAt : undefined,
            isInitialLoaded: true
        }
    });

    const currentCache = cache[activeCategory];
    const projects = currentCache?.projects || [];
    const hasMore = currentCache?.hasMore ?? false;
    const lastCreatedAt = currentCache?.lastCreatedAt;
    const isInitialLoaded = currentCache?.isInitialLoaded ?? false;

    const loadMoreRef = useRef<HTMLDivElement>(null);

    // Fetch initial batch for a category if not in cache
    useEffect(() => {
        if (isInitialLoaded) return;

        const categoryToFetch = activeCategory;
        const fetchInitial = async () => {
            setLoading(true);
            const { success, data } = await getPublishedCaseStudies(categoryToFetch, undefined, 10);

            if (success && data) {
                setCache(prev => ({
                    ...prev,
                    [categoryToFetch]: {
                        projects: data,
                        hasMore: data.length === 10,
                        lastCreatedAt: data.length > 0 ? data[data.length - 1].createdAt : undefined,
                        isInitialLoaded: true
                    }
                }));
            }
            setLoading(false);
        };

        fetchInitial();
    }, [activeCategory, isInitialLoaded]);

    const loadMore = async () => {
        if (loading || !hasMore) return;

        const categoryToLoad = activeCategory;
        const cursorToLoad = lastCreatedAt;

        setLoading(true);
        const { success, data } = await getPublishedCaseStudies(categoryToLoad, cursorToLoad, 10);

        if (success && data) {
            setCache(prev => {
                const existing = prev[categoryToLoad];
                if (!existing) return prev;
                return {
                    ...prev,
                    [categoryToLoad]: {
                        ...existing,
                        projects: [...existing.projects, ...data],
                        hasMore: data.length === 10,
                        lastCreatedAt: data.length > 0 ? data[data.length - 1].createdAt : existing.lastCreatedAt
                    }
                };
            });
        }
        setLoading(false);
    };

    // Intersection Observer for Infinite Scroll
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loading && isInitialLoaded) {
                    loadMore();
                }
            },
            { threshold: 0.1 }
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        return () => observer.disconnect();
    }, [hasMore, loading, lastCreatedAt, activeCategory, isInitialLoaded]);

    return (
        <div className="space-y-12">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-12">
                {CATEGORIES.map((category) => (
                    <button
                        key={category}
                        onClick={() => setActiveCategory(category)}
                        className={`px-6 py-2 rounded-full text-sm font-bold uppercase tracking-widest transition-all ${activeCategory === category
                            ? "bg-black text-white"
                            : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                            }`}
                    >
                        {category}
                    </button>
                ))}
            </div>

            {/* Grid */}
            {projects.length > 0 ? (
                <Stagger key={activeCategory} className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    {projects.map((project) => (
                        <FadeInStaggerItem key={project.id} className="h-full">
                            <Link href={`/case-studies/${project.id}`} className="block h-full">
                                <TiltCard
                                    className="h-full bg-white border border-gray-200 p-6 md:p-8 shadow-sm hover:shadow-2xl transition-all duration-500 group relative overflow-hidden rounded-2xl"
                                    innerClassName="h-full flex flex-col"
                                >
                                     <div className="aspect-[16/10] mb-6 relative">
                                        {project.coverImage && (
                                            <div className="absolute inset-0 transform scale-95 translate-y-3 blur-xl opacity-60 z-0 pointer-events-none transition-transform duration-300 group-hover:translate-y-4">
                                                <Image
                                                    src={project.coverImage}
                                                    alt=""
                                                    fill
                                                    className="object-cover rounded-xl"
                                                    unoptimized
                                                />
                                            </div>
                                        )}
                                        <div className="w-full h-full bg-gray-100 relative overflow-hidden rounded-xl border border-black/5 shadow-inner z-10">
                                            {project.coverImage ? (
                                                <Image
                                                    src={project.coverImage}
                                                    alt={project.title}
                                                    fill
                                                    className="object-cover"
                                                    unoptimized
                                                />
                                            ) : (
                                                <div className="absolute inset-0 flex items-center justify-center text-gray-400 font-medium">No Image</div>
                                            )}
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors"></div>
                                        </div>
                                    </div>
                                    <h3 className="text-2xl font-bold mb-2 group-hover:underline underline-offset-4 decoration-2 uppercase tracking-tighter">
                                        {project.title}
                                    </h3>
                                    <div className="flex items-center gap-2 mb-4">
                                        <span className="h-[1px] w-4 bg-gray-300"></span>
                                        <p className="text-gray-400 font-mono text-[10px] uppercase tracking-widest">{project.category}</p>
                                    </div>
                                    <p className="text-gray-500 text-sm leading-relaxed line-clamp-2 flex-1">
                                        {project.description || project.excerpt}
                                    </p>
                                </TiltCard>
                            </Link>
                        </FadeInStaggerItem>
                    ))}
                </Stagger>
            ) : (
                !loading && (
                    <div className="text-center py-20 text-gray-400 flex flex-col items-center gap-4">
                        <p className="text-xl font-medium">No projects found in this category.</p>
                        <button
                            onClick={() => setActiveCategory("All")}
                            className="text-black font-bold uppercase tracking-widest border-b-2 border-black pb-1 hover:text-gray-600 hover:border-gray-600 transition-colors"
                        >
                            View All Works
                        </button>
                    </div>
                )
            )}

            {/* Loading State & Load More Trigger */}
            <div ref={loadMoreRef} className="py-10 flex justify-center">
                {loading && (
                    <div className="flex items-center gap-2 text-gray-400 animate-pulse">
                        <Loader2 className="h-5 w-5 animate-spin text-black" />
                        <span className="text-xs font-bold uppercase tracking-widest">Loading...</span>
                    </div>
                )}
            </div>
        </div>
    );
}
